import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Tooltip,
  IconButton,
} from "@mui/material";
import CloseIcon from "@material-ui/icons/Close";
import ReactPlayer from "react-player";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import DoNotDisturbAltOutlinedIcon from "@mui/icons-material/DoNotDisturbAltOutlined";
import CTLNotification from "application/components/CTLNotification";
import CTLDialogContent from "application/components/styles/CTLDialogContent";
import { useMaterialUIController } from "context";
import variables from "globals/variables";

export default function PlayFile({ row }) {
  const [open, setOpen] = useState(false);
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
    pagename: "",
    status: "",
  });
  const abortControllerRef = useRef(null);

  const recordFilename = row?.original?.path ?? null;

  const fileUrl = recordFilename
    ? `${variables?.app?.services ?? ""}auth/playFile?fileName=${encodeURIComponent(
        recordFilename
      )}`
    : "";

  const handleClickOpen = async () => {
    if (!recordFilename) {
      setNotify({
        isOpen: true,
        status: "Unable To Play File: File Not Exist",
        type: "error",
        pagename: "voice picker",
      });
      return;
    }

    try {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      // Only try to fetch if fileUrl is valid
      if (fileUrl) {
        await fetch(fileUrl, { method: "GET", signal: abortController.signal });
        setOpen(true);
      } else {
        throw new Error("Invalid file URL");
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error while playing audio:", error);
        setNotify({
          isOpen: true,
          status: "Error Playing File",
          type: "error",
          pagename: "voice picker",
        });
      }
    }
  };

  const handleClose = () => {
    setOpen(false);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  return (
    <div>
      <Tooltip title={!recordFilename ? "File Not Exist" : "Play Audio"}>
        <IconButton
          variant="contained"
          color={recordFilename ? "success" : "error"}
          style={{ margin: "10px" }}
          onClick={handleClickOpen}
        >
          {recordFilename ? (
            <PlayCircleIcon color="success" />
          ) : (
            <DoNotDisturbAltOutlinedIcon color="error" />
          )}
        </IconButton>
      </Tooltip>

      <Dialog
        onClose={handleClose}
        open={open}
        sx={(theme) => CTLDialogContent(theme, { darkMode })}
      >
        <DialogTitle sx={(theme) => CTLDialogContent(theme, { darkMode })}>
          Play Recording File
          <IconButton
            color="error"
            aria-label="close"
            onClick={handleClose}
            sx={{ position: "absolute", right: "8px", top: "8px" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={(theme) => CTLDialogContent(theme, { darkMode })}>
          {fileUrl ? (
            <ReactPlayer
              key={open ? fileUrl : "closed"}
              url={fileUrl}
              width="500px"
              height="60px"
              playing={open}
              controls={true}
            />
          ) : (
            <p style={{ color: "red" }}>Invalid or missing file URL</p>
          )}
        </DialogContent>
      </Dialog>

      <CTLNotification notify={notify} setNotify={setNotify} />
    </div>
  );
}
